// =====================================================================================
// FILE: src/App.js (WITH DYNAMIC CATEGORIES + DYNAMIC TABLE NAMES + GAME TIMELINE UPDATES + IN-GAME COLUMN FIX)
// =====================================================================================

import React, { useMemo, useState, useRef, useEffect } from "react";
import "./App.css";
import { DndContext, closestCenter } from "@dnd-kit/core";

// Constants
import { THEME } from "./constants/theme";
import {
  DEFAULT_VIEW,
  DEFAULT_NEW_STATUS,
} from "./constants/views";
import { getVenueFromViewName, getVisibleColsForVenue, gridTemplate, minWidth, buildStickyLeft } from "./constants/columns";
import { groupCategoriesByPhase, shouldUsePhasing } from "./constants/phases";
import { isIptvOperatorOnly } from "./constants/categories";

// Utils
import { cleanMulti } from "./utils/categoryUtils";
import { isRowLocked } from "./utils/statusColors";

// Services
import { createApiAdapter } from "./services/apiAdapter";
import { fetchTableNames, fetchCategoryOptions } from "./services/tableMetadata";
import { fetchIptvOptions } from "./services/iptvOptions";
import { fetchAvailableViews, viewNameToFixtureKey } from "./services/viewLoader";
import {
  syncSourceToOperatorViews,
  shouldSyncToOperator,
  getSourceTableIdForMode,
  isBidirectionalSyncConfigured,
} from "./services/bidirectionalSync";

// Hooks
import { useAssetCache } from "./hooks/useAssetCache";
import { useDataLoader } from "./hooks/useDataLoader";

// Components
import Header from "./components/layout/Header";
import CategorySection from "./components/table/CategorySection";
import EditModal from "./components/modal/EditModal";
import AddModal from "./components/modal/AddModal";
import { showAlert, showConfirm } from "./components/ui/Toast";
import AuthGate from "./components/auth/AuthGate";
import UpdateBanner from "./components/UpdateBanner";
import { useAuth } from "./context/AuthContext";
import { useMatchLock } from "./AppShell";
import { countMatches } from "./components/ui/SearchBar";
import Confetti, { useConfettiTrigger } from "./components/ui/Confetti";
// DISABLED: Tawk chat not fit for purpose, keeping for future Airtable chat
// import SlackChatWidget from "./components/ui/SlackChatWidget";
import StickyHorizontalScroll from "./components/ui/StickyHorizontalScroll";
import AssetGallery from "./components/ui/AssetGallery";
import { usePresence } from "./context/PresenceContext";
import PhaseHeader from "./components/ui/PhaseHeader";
import TVCPlaylistView from "./pages/TVCPlaylistView";
import LEDPlaylistView from "./pages/LEDPlaylistView";
import CloneMatchModal from "./components/modal/CloneMatchModal";
import BulkAssetSwapModal from "./components/modal/BulkAssetSwapModal";

// ✅ Helper to extract unique categories from items, preserving order from Airtable
function extractCategories(items) {
  const seen = new Set();
  const ordered = [];
  
  // Preserve the order categories first appear in the data
  items.forEach(item => {
    const cat = String(item.category || "").trim();
    if (cat && !seen.has(cat)) {
      seen.add(cat);
      ordered.push(cat);
    }
  });
  
  return ordered;
}

// ✅ Team List view options (outside component to avoid re-creation on each render)
const TEAM_LIST_VIEWS = ["MCG Team List", "Marvel Team List"];
const DEFAULT_TEAM_VIEW = "MCG Team List";

export default function App() {
  // ============================================================================
  // URL PARAMS & MODE DETECTION
  // ============================================================================
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const rawMode = String(params.get("mode") || "out").trim().toLowerCase();

  // ✅ Get current user info for edit tracking and role checking
  const { userName, userEmail, isVenueOps, isLumen8Staff, allowedVenues } = useAuth();
  const editorName = userName || userEmail || "Unknown";

  // ✅ Match Lock — per-match mode locking
  const { isModeLocked, setLockableViews } = useMatchLock();

  const isTeam = rawMode === "team" || rawMode === "teamlist" || rawMode === "team-list" || rawMode === "t";
  const isLed = !isTeam && (rawMode === "led" || rawMode === "ledentitlements" || rawMode === "led-entitlements" || rawMode === "entitlements" || rawMode === "l");
  const isIptv = !isLed && !isTeam && (rawMode === "iptv" || rawMode === "ip-tv" || rawMode === "tv" || rawMode === "iptvmode" || rawMode === "p");
  const isInGame = !isLed && !isTeam && !isIptv && (rawMode === "ingame" || rawMode === "in-game" || rawMode === "in" || rawMode === "i" || rawMode === "game");
  const isTvc = !isLed && !isTeam && !isIptv && !isInGame && (rawMode === "tvc" || rawMode === "tvcs");
  const isCommLed = !isLed && !isTeam && !isIptv && !isInGame && !isTvc && (rawMode === "commled" || rawMode === "comm-led" || rawMode === "ledplaylist" || rawMode === "led-playlist");

  // ✅ Determine current mode key for API calls
  const currentMode = isTeam ? 'team' : isLed ? 'led' : isIptv ? 'iptv' : isInGame ? 'ingame' : isTvc ? 'tvc' : isCommLed ? 'commled' : 'out';

  // ✅ TVC Allocations header state (lifted from TVCAllocationsView for subheader)
  const [tvcHeaderInfo, setTvcHeaderInfo] = useState({ rounds: [], selectedRound: null, infoText: "" });
  const setTvcVenue = (v) => setTvcHeaderInfo((prev) => ({ ...prev, selectedRound: v }));

  // ✅ LED Allocations header state
  const [ledHeaderInfo, setLedHeaderInfo] = useState(() => {
    const cached = sessionStorage.getItem('venueflow_led_round');
    return { rounds: [], selectedRound: cached || null, infoText: "" };
  });
  const setLedVenue = (v) => {
    sessionStorage.setItem('venueflow_led_round', v || '');
    setLedHeaderInfo((prev) => ({ ...prev, selectedRound: v }));
  };

  // ✅ DYNAMIC VIEWS STATE - moved up for early access
  const [dynamicViews, setDynamicViews] = useState(() => {
    try {
      // Try mode-specific cache first
      const cacheKey = `venueflow_dynamic_views_${currentMode}`;
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) return JSON.parse(cached);
    } catch (e) {}
    return [];
  });

  // ✅ State for auto-selected view (avoids page reload jitter)
  const [autoSelectedView, setAutoSelectedView] = useState(null);
  
  // ✅ Track if initial view resolution is complete (prevents jitter)
  const [viewsResolved, setViewsResolved] = useState(() => {
    // If we have a URL view param, we're already resolved
    const urlView = params.get("view");
    return !!(urlView && urlView !== "" && urlView !== "null");
  });

  // ✅ For Team List, use Team List views only
  // For all other modes, use fixture views only (ignore Team List views)
  const rawView = params.get("view") || "";
  const isTeamListView = rawView.toLowerCase().includes("team list");
  
  // ✅ Get the first available dynamic view as default (multi-club support)
  const firstDynamicView = dynamicViews.length > 0 ? dynamicViews[0] : null;
  const effectiveDefaultView = DEFAULT_VIEW || firstDynamicView;
  
  // ✅ Use autoSelectedView as fallback when no URL view is present
  const viewName = isTeam 
    ? (rawView || DEFAULT_TEAM_VIEW)  // Team mode: use Team List view
    : (isTeamListView ? effectiveDefaultView : (rawView || autoSelectedView || effectiveDefaultView || ""));  // Other modes: prefer URL, then auto-selected
  
  // ✅ Use dynamic fixture key conversion instead of hardcoded mapping
  const fixtureKey = viewNameToFixtureKey(viewName) || params.get("fixture") || "";
  const fixtureForWrites = isTeam ? "" : fixtureKey;

  const venue = useMemo(() => getVenueFromViewName(viewName), [viewName]);

  // ✅ Match Lock — check if current mode is locked for this match (non-Lumen8 users only)
  const isMatchLocked = useMemo(() => {
    if (isLumen8Staff) return false; // Lumen8 staff bypass locks
    return isModeLocked(viewName, currentMode);
  }, [isLumen8Staff, isModeLocked, viewName, currentMode]);

  // ✅ Register available fixture views for the Lock Match sidebar
  useEffect(() => {
    if (dynamicViews.length > 0) {
      setLockableViews(dynamicViews);
    }
  }, [dynamicViews, setLockableViews]);

  // ============================================================================
  // PRESENCE - Real-time Collaboration
  // ============================================================================
  const { setCurrentPage } = usePresence();
  
  // Track current page for presence (update when view changes)
  useEffect(() => {
    const pageName = `matchflow-${currentMode}-${viewName || 'home'}`;
    setCurrentPage(pageName);
  }, [currentMode, viewName, setCurrentPage]);

  // ============================================================================
  // ✅ DYNAMIC CATEGORIES STATE (from Airtable field options)
  // ============================================================================
  const [definedCategories, setDefinedCategories] = useState(() => {
    try {
      const cacheKey = `venueflow_categories_${currentMode}`;
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) return JSON.parse(cached);
    } catch (e) {}
    return [];
  });

  // ============================================================================
  // ✅ NEW: DYNAMIC TABLE NAMES STATE
  // ============================================================================
  const [tableNames, setTableNames] = useState(() => {
    try {
      const cached = sessionStorage.getItem('venueflow_table_names');
      if (cached) return JSON.parse(cached);
    } catch (e) {}
    return null;
  });

  // ✅ NEW: DYNAMIC IPTV OPTIONS STATE
  const [iptvOptions, setIptvOptions] = useState(() => {
    try {
      const cached = sessionStorage.getItem('venueflow_iptv_options');
      if (cached) return JSON.parse(cached);
    } catch (e) {}
    return {
      timing: ["Pre-Match", "During ALL Match", "Quarter Time", "Half Time", "Three Quarter Time", "Full Time", "Post Match"],
      location: ["Premium Dining", "Corporate Boxes", "General Admission", "Harrison Room", "Ryder Room"]
    };
  });
  
  const modeLabel = useMemo(() => {
    if (!tableNames) return "Loading...";
    if (isTeam) return tableNames.team;
    if (isLed) return tableNames.led;
    if (isIptv) return tableNames.iptv;
    if (isInGame) return tableNames.ingame;
    if (isTvc) return "TVC Playlist";
    if (isCommLed) return "LED Playlist";
    return tableNames.out;
  }, [isTeam, isLed, isIptv, isInGame, isTvc, isCommLed, tableNames]);

  // ============================================================================
  // API ADAPTER
  // ============================================================================
  const api = useMemo(() => createApiAdapter(isInGame, isLed, isTeam, isIptv), [isInGame, isLed, isTeam, isIptv]);

  // ============================================================================
  // STATE
  // ============================================================================
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null); // ✅ Track last save time
  const [lastInlineCreatedId, setLastInlineCreatedId] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [addingCategory, setAddingCategory] = useState(null);
  
  // ✅ NEW: Search state
  const [searchTerm, setSearchTerm] = useState("");
  
  // ✅ NEW: Bulk selection state
  const [selectedRowIds, setSelectedRowIds] = useState(new Set());
  const [bulkCopyTargetCategories, setBulkCopyTargetCategories] = useState([]); // ✅ Changed to array for multi-select

  // ✅ NEW: Asset gallery state
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);

  // ✅ NEW: Clone match + Bulk asset swap modal state
  const [isCloneModalOpen, setIsCloneModalOpen] = useState(false);
  const [isBulkSwapModalOpen, setIsBulkSwapModalOpen] = useState(false);

  // ✅ NEW: Undo history state
  const [undoHistory, setUndoHistory] = useState([]);
  const MAX_UNDO_HISTORY = 20; // Keep last 20 actions

  const tableScrollRef = useRef(null);

  // ============================================================================
  // CUSTOM HOOKS
  // ============================================================================
  const { assetCache, warmCache, updateCache } = useAssetCache();

  // ============================================================================
  // UNDO SYSTEM HELPERS
  // ============================================================================
  
  // Push an action to undo history
  const pushUndo = (action) => {
    setUndoHistory(prev => {
      const next = [action, ...prev].slice(0, MAX_UNDO_HISTORY);
      return next;
    });
  };

  // Perform undo of the last action
  const performUndo = async () => {
    if (undoHistory.length === 0 || saving) return;
    
    const lastAction = undoHistory[0];
    
    try {
      setSaving(true);
      isSavingRef.current = true;
      
      switch (lastAction.type) {
        case 'create':
          // Undo create = delete operator rows then delete the created row
          await api.deleteOperatorRows(lastAction.rowId);
          await api.del(lastAction.rowId);
          showAlert(`Undid: Created "${lastAction.rowName || 'row'}"`, "info");
          break;
          
        case 'delete':
          // Undo delete = recreate the row with original data
          const recreated = await api.create(lastAction.rowData);
          if (recreated?.id) {
            showAlert(`Undid: Deleted "${lastAction.rowData.name || 'row'}"`, "info");
          }
          break;
          
        case 'edit':
          // Undo edit = restore previous values
          await api.update(lastAction.rowId, lastAction.previousData);
          showAlert(`Undid: Edit to "${lastAction.rowName || 'row'}"`, "info");
          break;
          
        case 'bulk_create':
          // Undo bulk create = delete operator rows then delete all created rows
          for (const rowId of lastAction.rowIds) {
            // rowIds don't carry names — find from current items if possible
            const rowItem = items.find(i => i.id === rowId);
            if (rowItem) await api.deleteOperatorRows(rowId);
            await api.del(rowId);
          }
          showAlert(`Undid: Created ${lastAction.rowIds.length} rows`, "info");
          break;
          
        case 'bulk_delete':
          // Undo bulk delete = recreate all deleted rows
          for (const rowData of lastAction.rowsData) {
            await api.create(rowData);
          }
          showAlert(`Undid: Deleted ${lastAction.rowsData.length} rows`, "info");
          break;
          
        default:
          showAlert("Cannot undo this action", "warning");
          return;
      }
      
      // Remove the action from history
      setUndoHistory(prev => prev.slice(1));
      
      // Reload data
      await load({ silent: true });
      
    } catch (e) {
      console.error("Undo failed:", e);
      showAlert("Undo failed: " + (e?.message || e), "error");
    } finally {
      isSavingRef.current = false;
      setSaving(false);
      setLastSaved(Date.now());
    }
  };

  const canUndo = undoHistory.length > 0 && !saving;

  const { items, setItems, status, load, isDraggingRef, isSavingRef, showModal, editingCell, setEditingCell, verifiedUpdate, registerPendingSave } = useDataLoader(
    api,
    fixtureKey,
    viewName,
    [],
    warmCache,
    isInGame,
    isLed,
    isTeam,
    isIptv
  );

  // ✅ CATEGORY_ORDER: Extract categories from current view's data only
  // This ensures only categories with content in this view are shown
  const CATEGORY_ORDER_RAW = useMemo(() => extractCategories(items), [items]);
  
  // ✅ "Venue Content" category is only visible to venue_ops and Lumen8 staff
  // Club users and other roles cannot see this category
  const VENUE_CONTENT_CATEGORY = "Venue Content";
  const CATEGORY_ORDER = useMemo(() => {
    let cats = CATEGORY_ORDER_RAW;
    
    // Hide "Venue Content" from non-venue-ops / non-Lumen8 staff
    if (!isVenueOps && !isLumen8Staff) {
      cats = cats.filter(cat => cat !== VENUE_CONTENT_CATEGORY);
    }
    
    // Hide operator-only IPTV categories from MatchFlow Builder (club view)
    // These are managed directly by operators in the Operator Dashboard
    if (isIptv) {
      cats = cats.filter(cat => !isIptvOperatorOnly(cat));
    }
    
    return cats;
  }, [CATEGORY_ORDER_RAW, isVenueOps, isLumen8Staff, isIptv]);

  // ✅ NEW: Group categories by phase for Game Timeline mode
  const usePhasing = shouldUsePhasing(isInGame, isLed, isTeam, isIptv);
  const phasedCategories = useMemo(() => {
    if (!usePhasing) return null;
    return groupCategoriesByPhase(CATEGORY_ORDER);
  }, [CATEGORY_ORDER, usePhasing]);

  // ✅ Confetti trigger when all items approved
  const confettiTrigger = useConfettiTrigger(items);

  // ✅ Catch-up sync: detect items with assets that haven't been synced to operator.
  // Runs after each poll cycle. Tracks synced record IDs in a ref so each item is
  // only synced once per session — prevents redundant API calls on every poll.
  const syncedRecordIdsRef = useRef(new Set());
  const catchupActiveRef = useRef(false);

  useEffect(() => {
    if (!shouldSyncToOperator({ isTeam, isInGame, isLed, isIptv }) || !isBidirectionalSyncConfigured()) return;
    if (catchupActiveRef.current || items.length === 0) return;

    const CG_FIELDS = ['bigGfxVideosId', 'cornerGfxVideosId', 'superFullId', 'super169Id', 'superSideId'];
    const RIBBON_FIELDS = ['ledAllTypesId', 'mcgInterchangeId', 'marvelInterchangeId', 'marvel360Id', 'mcg360Id', 'mcgTiledLedId', 'marvelTiledLedId'];

    // Find items with assets that we haven't synced this session
    const unsyncedItems = items.filter(item => {
      if (!item.id || syncedRecordIdsRef.current.has(item.id)) return false;
      // Skip Example Content
      if ((item.contentStatus || '').toLowerCase().includes('example content')) return false;
      // Skip items where Has Content formula says No — placeholder rows with no real assets
      if (item.hasContent && !item.hasContent.toLowerCase().startsWith('yes')) return false;
      const hasCg = CG_FIELDS.some(f => item[f] && typeof item[f] === 'string' && item[f].startsWith('rec'));
      const hasRibbon = RIBBON_FIELDS.some(f => item[f] && typeof item[f] === 'string' && item[f].startsWith('rec'));
      return hasCg || hasRibbon;
    });

    if (unsyncedItems.length === 0) return;

    // Run catch-up sync in background (non-blocking, rate-limited)
    catchupActiveRef.current = true;
    const sourceTableId = getSourceTableIdForMode({ isTeam });

    (async () => {
      for (const item of unsyncedItems) {
        try {
          await syncSourceToOperatorViews(item.id, item, {
            venue,
            sourceTableId,
            preserveOperatorFields: true, // Don't reset Content Status — just ensure operator rows exist
          });
          syncedRecordIdsRef.current.add(item.id);
        } catch (err) {
          console.warn(`[CatchupSync] Failed for "${item.name}":`, err.message);
        }
      }
      catchupActiveRef.current = false;
    })();
  }, [items, isTeam, isInGame, isLed, isIptv, venue]);

  // ============================================================================
  // ✅ FETCH TABLE NAMES & IPTV OPTIONS ON MOUNT
  // ============================================================================
  useEffect(() => {
    fetchTableNames()
      .then(setTableNames)
      .catch(() => {
        setTableNames({
          out: 'Out of Game',
          ingame: 'In Game',
          led: 'LED Entitlements',
          team: 'Team List',
          iptv: 'IPTV',
          tvc: 'TVC Playlist',
        });
      });
  }, []);

  // ✅ Fetch dynamic views from Airtable Metadata API (per mode)
  // Helper to check if a view matches allowed venues (defined here for useEffect access)
  const filterViewsByVenue = React.useCallback((views, venues) => {
    if (!venues || !Array.isArray(venues) || venues.length === 0) return views;
    return views.filter(viewName => {
      const viewLower = viewName.toLowerCase();
      return venues.some(venue => {
        const venueLower = venue.toLowerCase();
        if (venueLower === 'marvel' || venueLower === 'mrv') {
          return viewLower.includes('marvel') || viewLower.includes('mrv');
        }
        if (venueLower === 'mcg') {
          return viewLower.includes('mcg');
        }
        return viewLower.includes(venueLower);
      });
    });
  }, []);

  useEffect(() => {
    // Use mode-specific cache key
    const cacheKey = `venueflow_dynamic_views_${currentMode}`;
    
    fetchAvailableViews(currentMode)
      .then((views) => {
        // ✅ Filter views for venue_ops based on allowed venues
        const filteredViews = isVenueOps ? filterViewsByVenue(views, allowedVenues) : views;
        
        setDynamicViews(filteredViews);
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify(filteredViews));
        } catch (e) {}
        
        // ✅ Auto-select first view if no view is currently selected (multi-club support)
        // Use state instead of page reload to avoid jitter
        if (filteredViews.length > 0 && !isTeam) {
          const currentView = params.get("view");
          // Also check if current view is allowed for this user
          const currentViewAllowed = !isVenueOps || !allowedVenues?.length || 
            filterViewsByVenue([currentView], allowedVenues).length > 0;
          
          if (!currentView || currentView === "" || currentView === "null" || !currentViewAllowed) {
            // ✅ Check sessionStorage for shared match selection (from Operator View)
            let sharedMatch = null;
            try { sharedMatch = sessionStorage.getItem('venueflow_selected_match'); } catch (e) {}
            
            // Use shared match if it exists in this mode's views, otherwise first available
            const targetView = (sharedMatch && filteredViews.includes(sharedMatch))
              ? sharedMatch
              : filteredViews[0];
            
            // Set auto-selected view in state (no page reload!)
            setAutoSelectedView(targetView);
            
            // Update URL for bookmarking/sharing without reload
            const p = new URLSearchParams(window.location.search);
            p.set("view", targetView);
            const mapped = viewNameToFixtureKey(targetView);
            if (mapped) p.set("fixture", mapped);
            const newUrl = `${window.location.pathname}?${p.toString()}`;
            window.history.replaceState({}, '', newUrl);
            
            // Sync back to sessionStorage
            try { sessionStorage.setItem('venueflow_selected_match', targetView); } catch (e) {}
          } else if (currentView) {
            // ✅ URL has a valid view — sync it to sessionStorage for Operator View
            try { sessionStorage.setItem('venueflow_selected_match', currentView); } catch (e) {}
          }
        }
        
        // ✅ Mark views as resolved to stop loading state
        setViewsResolved(true);
      })
      .catch((err) => {
        console.warn(`Failed to fetch views for mode ${currentMode}:`, err);
        // Try to load from cache on error
        try {
          const cached = sessionStorage.getItem(cacheKey);
          if (cached) {
            const cachedViews = JSON.parse(cached);
            const filteredCached = isVenueOps ? filterViewsByVenue(cachedViews, allowedVenues) : cachedViews;
            setDynamicViews(filteredCached);
          }
        } catch (e) {}
        // Still mark as resolved even on error
        setViewsResolved(true);
      });
  }, [currentMode, isTeam, params, isVenueOps, allowedVenues, filterViewsByVenue]);

  // ✅ Fetch dynamic categories from Airtable Metadata API (per mode)
  useEffect(() => {
    fetchCategoryOptions(currentMode)
      .then((categories) => {
        setDefinedCategories(categories);
      })
      .catch((err) => {
        console.warn(`Failed to fetch categories for mode ${currentMode}:`, err);
      });
  }, [currentMode]);

  useEffect(() => {
    fetchIptvOptions()
      .then(setIptvOptions)
      .catch(() => {
        console.warn('Failed to fetch IPTV options, using fallback');
      });
  }, []);

  // ============================================================================
  // VISIBLE COLUMNS
  // ============================================================================
  // VISIBLE COLUMNS - HARDCODED PER TABLE (see columns.js)
  // ============================================================================
  const visibleCols = useMemo(() => {
    // Determine if this is In-Game Commercial (LED table renamed)
    const isInGameCommercial = isLed && tableNames?.led && tableNames.led.toLowerCase().includes('in-game');
    
    // Get columns from hardcoded configuration
    return getVisibleColsForVenue(venue, { 
      isInGame,
      isInGameCommercial,
      isLed,
      isTeam, 
      isIptv 
    });
  }, [venue, isLed, isIptv, isInGame, isTeam, tableNames]);

  const visibleKeys = useMemo(() => new Set(visibleCols.map((c) => c.key)), [visibleCols]);
  const template = useMemo(() => gridTemplate(visibleCols), [visibleCols]);
  const stickyLeftMap = useMemo(() => buildStickyLeft(visibleCols), [visibleCols]);
  const MIN_WIDTH = useMemo(() => minWidth(visibleCols), [visibleCols]);

  // ============================================================================
  // GROUP ITEMS BY CATEGORY
  // ============================================================================
  const grouped = useMemo(() => {
    const map = new Map();
    
    CATEGORY_ORDER.forEach((c) => map.set(c, []));

    items.forEach((it) => {
      const cat = String(it.category || "").trim();
      if (!cat) return;
      
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat).push(it);
    });

    map.forEach((arr) => arr.sort((a, b) => (a.cue || 0) - (b.cue || 0)));
    
    return map;
  }, [items, CATEGORY_ORDER]);

  // ✅ NEW: Determine which categories have selected rows
  const categoriesWithSelections = useMemo(() => {
    const cats = new Set();
    items.forEach((item) => {
      if (selectedRowIds.has(item.id)) {
        cats.add(item.category);
      }
    });
    return cats;
  }, [items, selectedRowIds]);

  // ============================================================================
  // EFFECTS
  // ============================================================================
  
  useEffect(() => {
    setEditingCell(null);
    setLastInlineCreatedId(null);
    // ✅ NEW: Clear bulk selection when changing views/modes
    setSelectedRowIds(new Set());
    setBulkCopyTargetCategories([]); // ✅ Clear array
    // ✅ NEW: Clear undo history when changing views
    setUndoHistory([]);
  }, [rawMode, viewName, fixtureKey, setEditingCell]);

  // ============================================================================
  // DRAG AND DROP HANDLERS
  // ============================================================================
  
  const onDragStart = () => {
    isDraggingRef.current = true;
  };

  const onDragCancel = () => {
    isDraggingRef.current = false;
  };

  const onDragEnd = async ({ active, over }) => {
    isDraggingRef.current = false;
    if (!over || active.id === over.id) return;

    const a = items.find((i) => i.id === active.id);
    const b = items.find((i) => i.id === over.id);
    if (!a || !b) return;

    if (!isTeam) {
      if (String(a.fixture || "") !== String(b.fixture || "")) return;
    }

    const aCat = String(a.category || "").trim();
    const bCat = String(b.category || "").trim();
    if (aCat !== bCat) return;

    const cat = aCat;
    const arr = grouped.get(cat) || [];
    const oldIndex = arr.findIndex((i) => i.id === active.id);
    const newIndex = arr.findIndex((i) => i.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    const { arrayMove } = await import("@dnd-kit/sortable");
    const moved = arrayMove(arr, oldIndex, newIndex).map((it, i) => ({ ...it, cue: i + 1 }));

    setItems((prev) => {
      const m = new Map(moved.map((x) => [x.id, x]));
      return prev.map((p) => m.get(p.id) || p);
    });

    try {
      setSaving(true);
      isSavingRef.current = true;
      await api.updateCueOrder(moved);
      await load({ silent: true });
    } catch (e) {
      await load({ silent: false });
      showAlert("Save failed: " + (e?.message || e));
    } finally {
      isSavingRef.current = false;
      setSaving(false); setLastSaved(Date.now());
    }
  };

  // ============================================================================
  // HANDLERS
  // ============================================================================
  
  function nextCueForCategory(category) {
    const cat = String(category || "").trim();
    const arr = grouped.get(cat) || [];
    const maxCue = arr.reduce((m, it) => Math.max(m, Number(it.cue || 0)), 0);
    return maxCue + 1;
  }

  async function createEmptyRowForCategory(category) {
    const cat = String(category || "").trim();
    if (!cat) return;
    if (saving || showModal || editingCell) return;

    const cue = nextCueForCategory(cat);
    
    // ✅ For Team List, extract venue from view name (e.g., "MCG Team List" → "MCG")
    const destinationVenue = isTeam ? (viewName.includes("MCG") ? "MCG" : viewName.includes("Marvel") ? "Marvel Stadium" : "") : "";

    const payload = {
      name: "",
      clientSponsorName: "",
      notes: "",
      clubInstructions: "",
      fixture: fixtureForWrites,
      category: cat,
      cue,
      contentStatus: DEFAULT_NEW_STATUS,
      allocationId: "",
      rotations: null,
      bonusPriority: null,						  
      playerNumber: "", // ✅ Team List player number
      destinationVenue, // ✅ Team List venue (MCG or Marvel Stadium)
      iptvGfxId: null,
      selectTiming: [],
      selectLocation: [],
      bigGfxVideosId: null,
      cornerGfxVideosId: null,
      ledAllTypesId: null,
      bigTvcId: null,
      cornerTvcId: null,
      mcgTiledLedId: null,
      marvelTiledLedId: null,
      superFullId: null,
      super169Id: null,
      superSideId: null,
      mcgInterchangeId: null,
      marvelInterchangeId: null,
      marvel360Id: null,
      mcg360Id: null,
      // ✅ Track who created this row
      venueFlowEditor: editorName,
    };

    // ✅ Create on server first, but don't block UI with saving state
    isSavingRef.current = true;
    
    try {
      const created = await api.create(payload);
      
      if (created?.id) {
        // ✅ Track for undo
        pushUndo({
          type: 'create',
          rowId: created.id,
          rowName: '',
        });
        
        // ✅ Add row and start editing in one update
        setItems(prev => [...prev, { ...payload, id: created.id }]);
        setLastInlineCreatedId(created.id);
        // Small delay to ensure state is set before focusing
        setTimeout(() => {
          setEditingCell({ id: created.id, col: "name", draft: "" });
        }, 10);
      }
    } catch (e) {
      showAlert("Create row failed: " + (e?.message || e), "error");
    } finally {
      isSavingRef.current = false;
    }
  }

  async function cancelLastInlineCreatedRow() {
    if (!lastInlineCreatedId || saving) return;
    try {
      setEditingCell(null);
      setSaving(true);
      isSavingRef.current = true;
      // Delete any matching operator rows, then delete the row itself
      const inlineItem = items.find(i => i.id === lastInlineCreatedId);
      if (inlineItem?.name) await api.deleteOperatorRows(lastInlineCreatedId);
      await api.del(lastInlineCreatedId);
    } catch (e) {
      // Already deleted
    } finally {
      setLastInlineCreatedId(null);
      setEditingCell(null);
      isSavingRef.current = false;
      setSaving(false); setLastSaved(Date.now());
      await load({ silent: true }).catch(() => {});
    }
  }

  function openAdd(initialCategory) {
    setAddingCategory(initialCategory);
  }

  // ✅ Edit modal removed - using inline editing instead

  async function handleModalSave(formData) {
    if (!editingItem) return;

    try {
      setSaving(true);
      isSavingRef.current = true;

      const payload = {
        name: formData.name || "",
        clientSponsorName: formData.clientSponsorName || "",
        notes: formData.notes || "",
        clubInstructions: formData.clubInstructions || "",
        fixture: fixtureForWrites,
        category: editingItem.category || "",
        cue: Number(editingItem.cue || 0),
        contentStatus: editingItem.contentStatus || DEFAULT_NEW_STATUS,
        allocationId: formData.allocationId || "",
        rotations: formData.rotations || null,
        bonusPriority: formData.bonusPriority || null,													  
        iptvGfxId: formData.iptvGfxId || null,
        selectTiming: cleanMulti(formData.selectTiming),
        selectLocation: cleanMulti(formData.selectLocation),
        bigGfxVideosId: formData.bigGfxVideosId || null,
        cornerGfxVideosId: formData.cornerGfxVideosId || null,
        ledAllTypesId: formData.ledAllTypesId || null,
        bigTvcId: formData.bigTvcId || null,
        cornerTvcId: formData.cornerTvcId || null,
        mcgTiledLedId: formData.mcgTiledLedId || null,
        marvelTiledLedId: formData.marvelTiledLedId || null,
        superFullId: formData.superFullId || null,
        super169Id: formData.super169Id || null,
        superSideId: formData.superSideId || null,
        mcgInterchangeId: formData.mcgInterchangeId || null,
        marvelInterchangeId: formData.marvelInterchangeId || null,
        marvel360Id: formData.marvel360Id || null,
        mcg360Id: formData.mcg360Id || null,
      };

      await verifiedUpdate(editingItem.id, payload);
      setEditingItem(null);
      await load({ silent: true });
    } catch (e) {
      showAlert("Save failed: " + (e?.message || e));
      await load({ silent: true });
    } finally {
      isSavingRef.current = false;
      setSaving(false); setLastSaved(Date.now());
    }
  }

  async function handleCreate(formData) {
    try {
      setSaving(true);
      isSavingRef.current = true;

      const cat = String(formData.category || addingCategory || "").trim();
      const cue = nextCueForCategory(cat);
      
      // ✅ For Team List, extract venue from view name (e.g., "MCG Team List" → "MCG")
      const destinationVenue = isTeam ? (viewName.includes("MCG") ? "MCG" : viewName.includes("Marvel") ? "Marvel Stadium" : "") : "";

      const payload = {
        name: formData.name || "",
        clientSponsorName: formData.clientSponsorName || "",
        notes: formData.notes || "",
        clubInstructions: formData.clubInstructions || "",
        fixture: fixtureForWrites,
        category: cat,
        cue,
        contentStatus: DEFAULT_NEW_STATUS,
        allocationId: formData.allocationId || "",
        rotations: formData.rotations || null,
        bonusPriority: formData.bonusPriority || null,													  
        playerNumber: formData.playerNumber || "", // ✅ Team List player number
        destinationVenue, // ✅ Team List venue (MCG or Marvel)
        selectTiming: formData.selectTiming || [],
        selectLocation: formData.selectLocation || [],
        // Asset IDs from form
        iptvGfxId: formData.iptvGfxId || null,
        bigGfxVideosId: formData.bigGfxVideosId || null,
        cornerGfxVideosId: formData.cornerGfxVideosId || null,
        ledAllTypesId: formData.ledAllTypesId || null,
        bigTvcId: formData.bigTvcId || null,
        cornerTvcId: formData.cornerTvcId || null,
        mcgTiledLedId: formData.mcgTiledLedId || null,
        marvelTiledLedId: formData.marvelTiledLedId || null,
        superFullId: formData.superFullId || null,
        super169Id: formData.super169Id || null,
        superSideId: formData.superSideId || null,
        mcgInterchangeId: formData.mcgInterchangeId || null,
        marvelInterchangeId: formData.marvelInterchangeId || null,
        marvel360Id: formData.marvel360Id || null,
        mcg360Id: formData.mcg360Id || null,
        // ✅ Track who created this row
        venueFlowEditor: editorName,
      };
      
      // DEBUG: Log Team List create payload
      if (isTeam) {
        console.log("[handleCreate] Team List payload:", { isTeam, viewName, destinationVenue, playerNumber: payload.playerNumber });
      }

      const created = await api.create(payload);
      
      // ✅ Track for undo
      if (created?.id) {
        pushUndo({
          type: 'create',
          rowId: created.id,
          rowName: formData.name || '',
        });
        
        // ✅ Optimistic update - add row immediately to state
        setItems(prev => [...prev, { ...payload, id: created.id }]);

        // ✅ Direction 1: Sync source → operator views for newly created row.
        // Without this, rows created with assets (via AddModal) would not generate
        // operator rows until the user re-selected the asset inline.
        if (shouldSyncToOperator({ isTeam, isInGame, isLed, isIptv })) {
          const createdItem = { ...payload, id: created.id };
          const sourceTableId = getSourceTableIdForMode({ isTeam });
          syncSourceToOperatorViews(created.id, createdItem, {
            venue,
            sourceTableId,
            lastFingerprint: '',
          }).then((syncResult) => {
            if (syncResult && !syncResult.skipped) {
              console.log('[App] Operator sync after create:', syncResult);
              syncedRecordIdsRef.current.add(created.id);
            }
          }).catch((err) => {
            console.warn('[App] Operator sync after create failed (non-blocking):', err);
          });
        }
      }
      
      setAddingCategory(null);
      // Background sync to ensure we have any server-computed fields
      load({ silent: true });
    } catch (e) {
      showAlert("Create failed: " + (e?.message || e));
      await load({ silent: true });
    } finally {
      isSavingRef.current = false;
      setSaving(false); setLastSaved(Date.now());
    }
  }

  async function handleCopy(item) {
    try {
      setSaving(true);
      isSavingRef.current = true;

      const cat = String(item.category || "").trim() || (isTeam ? "Team List" : isIptv ? "General Content All Areas" : "Pre-Game");
      const cue = nextCueForCategory(cat);
      
      // ✅ For Team List, extract venue from view name (e.g., "MCG Team List" → "MCG")
      const destinationVenue = isTeam ? (viewName.includes("MCG") ? "MCG" : viewName.includes("Marvel") ? "Marvel Stadium" : "") : "";

      const payload = {
        name: item.name || "",
        clientSponsorName: item.clientSponsorName || "",
        notes: item.notes || "",
        clubInstructions: item.clubInstructions || "",
        fixture: fixtureForWrites,
        category: cat,
        cue,
        contentStatus: DEFAULT_NEW_STATUS,
        allocationId: item.allocationId || "",
        rotations: item.rotations || null,
        bonusPriority: item.bonusPriority || null,												  
        playerNumber: item.playerNumber || "", // ✅ Team List player number
        destinationVenue, // ✅ Team List venue (MCG or Marvel)
        iptvGfxId: item.iptvGfxId || null,
        selectTiming: cleanMulti(item.selectTiming),
        selectLocation: cleanMulti(item.selectLocation),
        bigGfxVideosId: item.bigGfxVideosId || null,
        cornerGfxVideosId: item.cornerGfxVideosId || null,
        ledAllTypesId: item.ledAllTypesId || null,
        bigTvcId: item.bigTvcId || null,
        cornerTvcId: item.cornerTvcId || null,
        mcgTiledLedId: item.mcgTiledLedId || null,
        marvelTiledLedId: item.marvelTiledLedId || null,
        superFullId: item.superFullId || null,
        super169Id: item.super169Id || null,
        superSideId: item.superSideId || null,
        mcgInterchangeId: item.mcgInterchangeId || null,
        marvelInterchangeId: item.marvelInterchangeId || null,
        marvel360Id: item.marvel360Id || null,
        mcg360Id: item.mcg360Id || null,
        // ✅ Track who copied this row
        venueFlowEditor: editorName,
      };

      const created = await api.create(payload);
      
      // ✅ Optimistic update - add copied row immediately
      if (created?.id) {
        setItems(prev => [...prev, { ...payload, id: created.id }]);

        // ✅ Direction 1: Sync source → operator views for copied row.
        // Copied rows inherit assets, so operator rows need to be created immediately.
        if (shouldSyncToOperator({ isTeam, isInGame, isLed, isIptv })) {
          const createdItem = { ...payload, id: created.id };
          const sourceTableId = getSourceTableIdForMode({ isTeam });
          syncSourceToOperatorViews(created.id, createdItem, {
            venue,
            sourceTableId,
            lastFingerprint: '',
          }).then((syncResult) => {
            if (syncResult && !syncResult.skipped) {
              console.log('[App] Operator sync after copy:', syncResult);
            }
          }).catch((err) => {
            console.warn('[App] Operator sync after copy failed (non-blocking):', err);
          });
        }
      }
      
      // Background sync
      load({ silent: true });
    } catch (e) {
      showAlert("Copy failed: " + (e?.message || e));
      await load({ silent: true });
    } finally {
      isSavingRef.current = false;
      setSaving(false); setLastSaved(Date.now());
    }
  }

  async function handleDelete(item) {
    if (isRowLocked(item.contentStatus)) {
      showAlert("This content has been built by the operator and is locked. Contact your Lumen8 content manager to unlock it.", "warning");
      return;
    }

    showConfirm(
      `Delete "${item.name || "this row"}"? This cannot be undone.`,
      async () => {
        try {
          setSaving(true);
          isSavingRef.current = true;

          // ✅ Store row data for undo BEFORE deleting
          const rowDataForUndo = {
            name: item.name || "",
            clientSponsorName: item.clientSponsorName || "",
            notes: item.notes || "",
            clubInstructions: item.clubInstructions || "",
            fixture: fixtureForWrites,
            category: item.category || "",
            cue: item.cue || 1,
            contentStatus: DEFAULT_NEW_STATUS, // Reset status on undo
            allocationId: item.allocationId || "",
            rotations: item.rotations || null,
            bonusPriority: item.bonusPriority || null,													  
            iptvGfxId: item.iptvGfxId || null,
            selectTiming: cleanMulti(item.selectTiming),
            selectLocation: cleanMulti(item.selectLocation),
            bigGfxVideosId: item.bigGfxVideosId || null,
            cornerGfxVideosId: item.cornerGfxVideosId || null,
            ledAllTypesId: item.ledAllTypesId || null,
            bigTvcId: item.bigTvcId || null,
            cornerTvcId: item.cornerTvcId || null,
            mcgTiledLedId: item.mcgTiledLedId || null,
            marvelTiledLedId: item.marvelTiledLedId || null,
            superFullId: item.superFullId || null,
            super169Id: item.super169Id || null,
            superSideId: item.superSideId || null,
            mcgInterchangeId: item.mcgInterchangeId || null,
            marvelInterchangeId: item.marvelInterchangeId || null,
            marvel360Id: item.marvel360Id || null,
            mcg360Id: item.mcg360Id || null,
          };

          // ✅ Optimistic update - remove from state immediately
          setItems(prev => prev.filter(x => x.id !== item.id));

          // ✅ Step 1: Delete matching rows from CG Operator & Ribbon Operator tables
          //    Uses Source Record ID field for exact matching (safe with duplicate names)
          await api.deleteOperatorRows(item.id);

          // ✅ Step 2: Delete the MatchFlow row itself
          await api.del(item.id);
          
          // ✅ Track for undo
          pushUndo({
            type: 'delete',
            rowData: rowDataForUndo,
            rowName: item.name || '',
          });

          const cat = String(item.category || "").trim();
          const remaining = (grouped.get(cat) || [])
            .filter((x) => x.id !== item.id)
            .sort((a, b) => (a.cue || 0) - (b.cue || 0))
            .map((x, idx) => ({ ...x, cue: idx + 1 }));

          if (remaining.length) {
            await api.updateCueOrder(remaining);
          }
          
          // Background sync
          load({ silent: true });
          
          showAlert("Row deleted (Undo available)", "success");
        } catch (e) {
          showAlert("Delete failed: " + (e?.message || e), "error");
          // Reload on error to restore state
          await load({ silent: true });
        } finally {
          isSavingRef.current = false;
          setSaving(false); setLastSaved(Date.now());
        }
      }
    );
  }

  // ============================================================================
  // MOVE SINGLE ROW TO DIFFERENT CATEGORY
  // ============================================================================
  async function handleMove(item, targetCategory) {
    if (!item || !targetCategory) return;
    
    // Check if row is locked
    if (isRowLocked(item.contentStatus)) {
      showAlert("Cannot move: This content has been built by the operator and is locked.", "warning");
      return;
    }

    try {
      setSaving(true);
      isSavingRef.current = true;
      
      // Get next cue for target category
      const targetItems = items.filter(i => i.category === targetCategory);
      const maxCue = targetItems.length > 0 
        ? Math.max(...targetItems.map(i => i.cue || 0)) 
        : 0;
      const nextCue = maxCue + 1;
      
      // Update the item's category and cue
      await api.update(item.id, { 
        category: targetCategory,
        cue: nextCue,
      });
      
      // Reload to get updated data
      await load({ silent: true });
      
      showAlert(`Moved to ${targetCategory}`, "success");
    } catch (e) {
      console.error("Move error:", e);
      showAlert("Move failed: " + (e?.message || e), "error");
    } finally {
      isSavingRef.current = false;
      setSaving(false); setLastSaved(Date.now());
    }
  }

  // ============================================================================
  // LED SHUFFLE SEQUENCE
  // ============================================================================
  async function shuffleLedSequence() {
    if (!isLed) return;
    
    showConfirm(
      "Shuffle LED entitlements? This will reorder: Toyota → Position 1, Telstra → Position 2, others distributed evenly.",
      async () => {
        try {
          setSaving(true);
          isSavingRef.current = true;

          const allocations = items.filter(it => it.category === "Allocations");
          const bonusAllocations = items.filter(it => it.category === "Bonus Allocations");

          const toyota = allocations.find(it => it.name?.toLowerCase().includes("toyota"));
          const telstra = allocations.find(it => it.name?.toLowerCase().includes("telstra"));
          const others = allocations.filter(it => 
            it.id !== toyota?.id && it.id !== telstra?.id
          );

          const newSequence = [];
          let cue = 1;

          if (toyota) newSequence.push({ id: toyota.id, cue: cue++ });
          if (telstra) newSequence.push({ id: telstra.id, cue: cue++ });
          for (const item of others) {
            newSequence.push({ id: item.id, cue: cue++ });
          }
          for (const item of bonusAllocations) {
            newSequence.push({ id: item.id, cue: cue++ });
          }

          await api.updateCueOrder(newSequence);
          await load({ silent: true });
          
          showAlert("LED sequence shuffled successfully!", "success");
        } catch (err) {
          console.error("Shuffle error:", err);
          showAlert("Shuffle failed: " + (err?.message || err), "error");
        } finally {
          setSaving(false); setLastSaved(Date.now());
          isSavingRef.current = false;
        }
      }
    );
  }

  // ============================================================================
  // BULK SELECTION HANDLERS
  // ============================================================================
  
  function toggleRowSelection(rowId) {
    setSelectedRowIds(prev => {
      const next = new Set(prev);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });
  }

  function clearSelection() {
    setSelectedRowIds(new Set());
    setBulkCopyTargetCategories([]);
  }

  async function handleBulkCopy() {
    if (bulkCopyTargetCategories.length === 0 || selectedRowIds.size === 0) return;
    
    const itemsToCopy = items.filter(item => selectedRowIds.has(item.id));
    
    if (itemsToCopy.length === 0) {
      showAlert("No items selected to copy", "warning");
      return;
    }

    try {
      setSaving(true);
      isSavingRef.current = true;
      
      // Copy to each selected category
      for (const targetCategory of bulkCopyTargetCategories) {
        // Determine next cue for this target category
        let nextCue = nextCueForCategory(targetCategory);
        
        // Copy each item to this category
        for (const item of itemsToCopy) {
          const payload = {
            name: item.name || "",
            clientSponsorName: item.clientSponsorName || "",
            notes: item.notes || "",
            clubInstructions: item.clubInstructions || "",
            fixture: fixtureForWrites,
            category: targetCategory,
            cue: nextCue++,
            contentStatus: DEFAULT_NEW_STATUS,
            allocationId: item.allocationId || "",
            rotations: item.rotations || null,
            bonusPriority: item.bonusPriority || null,													  
            iptvGfxId: item.iptvGfxId || null,
            selectTiming: cleanMulti(item.selectTiming),
            selectLocation: cleanMulti(item.selectLocation),
            bigGfxVideosId: item.bigGfxVideosId || null,
            cornerGfxVideosId: item.cornerGfxVideosId || null,
            ledAllTypesId: item.ledAllTypesId || null,
            bigTvcId: item.bigTvcId || null,
            cornerTvcId: item.cornerTvcId || null,
            mcgTiledLedId: item.mcgTiledLedId || null,
            marvelTiledLedId: item.marvelTiledLedId || null,
            superFullId: item.superFullId || null,
            super169Id: item.super169Id || null,
            superSideId: item.superSideId || null,
            mcgInterchangeId: item.mcgInterchangeId || null,
            marvelInterchangeId: item.marvelInterchangeId || null,
            marvel360Id: item.marvel360Id || null,
            mcg360Id: item.mcg360Id || null,
          };
          
          await api.create(payload);
        }
      }
      
      // Clear selection and reload
      clearSelection();
      await load({ silent: true });
      
      const categoriesText = bulkCopyTargetCategories.length === 1 
        ? bulkCopyTargetCategories[0] 
        : `${bulkCopyTargetCategories.length} categories`;
      
      showAlert(`Copied ${itemsToCopy.length} row${itemsToCopy.length !== 1 ? 's' : ''} to ${categoriesText}`, "success");
    } catch (e) {
      showAlert("Bulk copy failed: " + (e?.message || e));
      await load({ silent: true });
    } finally {
      isSavingRef.current = false;
      setSaving(false); setLastSaved(Date.now());
    }
  }

  async function handleBulkMove() {
    if (bulkCopyTargetCategories.length === 0 || selectedRowIds.size === 0) return;
    
    const itemsToMove = items.filter(item => selectedRowIds.has(item.id));
    
    if (itemsToMove.length === 0) {
      showAlert("No items selected to move", "warning");
      return;
    }

    // Check if any rows are locked
    const lockedItems = itemsToMove.filter(item => isRowLocked(item.contentStatus));
    if (lockedItems.length > 0) {
      showAlert(`Cannot move: ${lockedItems.length} row${lockedItems.length !== 1 ? 's are' : ' is'} built by the operator and locked. Deselect locked rows or contact Lumen8 to unlock.`, "warning");
      return;
    }

    // Confirm move
    const categoriesText = bulkCopyTargetCategories.length === 1 
      ? bulkCopyTargetCategories[0] 
      : `${bulkCopyTargetCategories.length} categories`;
    
    showConfirm(
      `Move ${itemsToMove.length} row${itemsToMove.length !== 1 ? 's' : ''} to ${categoriesText}? Original rows will be deleted.`,
      async () => {
        try {
          setSaving(true);
          isSavingRef.current = true;
          
          // Create copies in target categories first
          for (const targetCategory of bulkCopyTargetCategories) {
            let nextCue = nextCueForCategory(targetCategory);
            
            for (const item of itemsToMove) {
              const payload = {
                name: item.name || "",
                clientSponsorName: item.clientSponsorName || "",
                notes: item.notes || "",
                clubInstructions: item.clubInstructions || "",
                fixture: fixtureForWrites,
                category: targetCategory,
                cue: nextCue++,
                contentStatus: item.contentStatus || DEFAULT_NEW_STATUS,
                allocationId: item.allocationId || "",
                rotations: item.rotations || null,
                bonusPriority: item.bonusPriority || null,														  
                iptvGfxId: item.iptvGfxId || null,
                selectTiming: cleanMulti(item.selectTiming),
                selectLocation: cleanMulti(item.selectLocation),
                bigGfxVideosId: item.bigGfxVideosId || null,
                cornerGfxVideosId: item.cornerGfxVideosId || null,
                ledAllTypesId: item.ledAllTypesId || null,
                bigTvcId: item.bigTvcId || null,
                cornerTvcId: item.cornerTvcId || null,
                mcgTiledLedId: item.mcgTiledLedId || null,
                marvelTiledLedId: item.marvelTiledLedId || null,
                superFullId: item.superFullId || null,
                super169Id: item.super169Id || null,
                superSideId: item.superSideId || null,
                mcgInterchangeId: item.mcgInterchangeId || null,
                marvelInterchangeId: item.marvelInterchangeId || null,
                marvel360Id: item.marvel360Id || null,
                mcg360Id: item.mcg360Id || null,
              };
              
              await api.create(payload);
            }
          }
          
          // Delete original items — clean up operator tables first
          for (const item of itemsToMove) {
            await api.deleteOperatorRows(item.id);
            await api.del(item.id);
          }
          
          // Clear selection and reload
          clearSelection();
          await load({ silent: true });
          
          showAlert(`Moved ${itemsToMove.length} row${itemsToMove.length !== 1 ? 's' : ''} to ${categoriesText}`, "success");
        } catch (e) {
          showAlert("Bulk move failed: " + (e?.message || e), "error");
          await load({ silent: true });
        } finally {
          isSavingRef.current = false;
          setSaving(false); setLastSaved(Date.now());
        }
      }
    );
  }

  async function handleBulkDelete() {
    if (selectedRowIds.size === 0) return;
    
    const itemsToDelete = items.filter(item => selectedRowIds.has(item.id));
    
    if (itemsToDelete.length === 0) {
      showAlert("No items selected to delete", "warning");
      return;
    }

    // Check if any rows are locked
    const lockedItems = itemsToDelete.filter(item => isRowLocked(item.contentStatus));
    if (lockedItems.length > 0) {
      showAlert("Cannot delete: One or more rows have been built by the operator and are locked. Contact your Lumen8 content manager to unlock them.", "warning");
      return;
    }

    // Confirm deletion
    showConfirm(
      `Delete ${itemsToDelete.length} selected row${itemsToDelete.length !== 1 ? 's' : ''}? This cannot be undone.`,
      async () => {
        try {
          setSaving(true);
          isSavingRef.current = true;
          
          // ✅ Step 1: Delete matching operator rows for each item (by Source Record ID)
          for (const item of itemsToDelete) {
            await api.deleteOperatorRows(item.id);
          }

          // ✅ Step 2: Delete each MatchFlow row
          for (const item of itemsToDelete) {
            await api.del(item.id);
          }
          
          // Clear selection and reload
          clearSelection();
          await load({ silent: true });
          
          showAlert(`Deleted ${itemsToDelete.length} row${itemsToDelete.length !== 1 ? 's' : ''}`, "success");
        } catch (e) {
          showAlert("Bulk delete failed: " + (e?.message || e), "error");
          await load({ silent: true });
        } finally {
          isSavingRef.current = false;
          setSaving(false); setLastSaved(Date.now());
        }
      }
    );
  }

  function onInlineSave(recordId, patch) {
    const existing = items.find((x) => x.id === recordId);
    if (!existing) {
      setEditingCell(null);
      return;
    }

    if (isRowLocked(existing.contentStatus)) {
      showAlert("This content has been built by the operator and is locked. Contact your Lumen8 content manager to unlock it.", "warning");
      setEditingCell(null);
      return;
    }

    // ✅ BLOCK POLLING IMMEDIATELY - before any state changes
    isSavingRef.current = true;

    // ✅ Store previous data for undo
    const previousDataForUndo = {};
    Object.keys(patch).forEach(key => {
      previousDataForUndo[key] = existing[key];
    });

    // Note: Asset change detection removed - status is now controlled by coordinators via Operator View

    const sanitizedPatch = { ...patch };
    if (sanitizedPatch.selectTiming !== undefined)
      sanitizedPatch.selectTiming = cleanMulti(sanitizedPatch.selectTiming);
    if (sanitizedPatch.selectLocation !== undefined)
      sanitizedPatch.selectLocation = cleanMulti(sanitizedPatch.selectLocation);

    // ✅ Don't auto-change status - coordinators control status via Operator View
    const nextPatch = sanitizedPatch;

    // ✅ Track for undo (before applying changes)
    pushUndo({
      type: 'edit',
      rowId: recordId,
      rowName: existing.name || '',
      previousData: previousDataForUndo,
    });

    // ✅ OPTIMISTIC: Update local state IMMEDIATELY
    setItems((prev) =>
      prev.map((p) => {
        if (p.id !== recordId) return p;
        const next = { ...p };

        if (nextPatch.name != null) next.name = nextPatch.name;
        if (nextPatch.clientSponsorName != null) next.clientSponsorName = nextPatch.clientSponsorName;
        if (nextPatch.notes != null) next.notes = nextPatch.notes;
        if (nextPatch.clubInstructions != null) next.clubInstructions = nextPatch.clubInstructions;
        if (nextPatch.allocationId != null) next.allocationId = nextPatch.allocationId;
        if (nextPatch.rotations !== undefined) next.rotations = nextPatch.rotations || null;
        if (nextPatch.bonusPriority !== undefined) next.bonusPriority = nextPatch.bonusPriority || null;
        if (nextPatch.selectTiming !== undefined) next.selectTiming = cleanMulti(nextPatch.selectTiming);
        if (nextPatch.selectLocation !== undefined) next.selectLocation = cleanMulti(nextPatch.selectLocation);
        if (nextPatch.iptvGfxId !== undefined) next.iptvGfxId = nextPatch.iptvGfxId;
        if (nextPatch.bigGfxVideosId !== undefined) next.bigGfxVideosId = nextPatch.bigGfxVideosId;
        if (nextPatch.cornerGfxVideosId !== undefined) next.cornerGfxVideosId = nextPatch.cornerGfxVideosId;
        if (nextPatch.ledAllTypesId !== undefined) next.ledAllTypesId = nextPatch.ledAllTypesId;
        if (nextPatch.bigTvcId !== undefined) next.bigTvcId = nextPatch.bigTvcId;
        if (nextPatch.cornerTvcId !== undefined) next.cornerTvcId = nextPatch.cornerTvcId;
        if (nextPatch.mcgTiledLedId !== undefined) next.mcgTiledLedId = nextPatch.mcgTiledLedId;
        if (nextPatch.marvelTiledLedId !== undefined) next.marvelTiledLedId = nextPatch.marvelTiledLedId;
        if (nextPatch.superFullId !== undefined) next.superFullId = nextPatch.superFullId;
        if (nextPatch.super169Id !== undefined) next.super169Id = nextPatch.super169Id;
        if (nextPatch.superSideId !== undefined) next.superSideId = nextPatch.superSideId;
        if (nextPatch.mcgInterchangeId !== undefined) next.mcgInterchangeId = nextPatch.mcgInterchangeId;
        if (nextPatch.marvelInterchangeId !== undefined) next.marvelInterchangeId = nextPatch.marvelInterchangeId;
        if (nextPatch.marvel360Id !== undefined) next.marvel360Id = nextPatch.marvel360Id;
        if (nextPatch.mcg360Id !== undefined) next.mcg360Id = nextPatch.mcg360Id;
        if (nextPatch.contentStatus != null) next.contentStatus = nextPatch.contentStatus;
        if (nextPatch.playerNumber != null) next.playerNumber = nextPatch.playerNumber; // ✅ Team List player number
        
        // ✅ Update editor name for hover display
        next.venueFlowEditor = editorName;

        return next;
      })
    );

    // ✅ Build payload for server
    const payload = {
      name: nextPatch.name != null ? nextPatch.name : existing.name || "",
      clientSponsorName: nextPatch.clientSponsorName != null ? nextPatch.clientSponsorName : existing.clientSponsorName || "",
      notes: nextPatch.notes != null ? nextPatch.notes : existing.notes || "",
      clubInstructions: nextPatch.clubInstructions != null ? nextPatch.clubInstructions : existing.clubInstructions || "",
      fixture: fixtureForWrites,
      category: existing.category || "",
      cue: Number(existing.cue || 0),
      contentStatus: nextPatch.contentStatus != null ? nextPatch.contentStatus : existing.contentStatus || DEFAULT_NEW_STATUS,
      allocationId: nextPatch.allocationId != null ? nextPatch.allocationId : existing.allocationId || "",
      rotations: nextPatch.rotations !== undefined ? (nextPatch.rotations || null) : (existing.rotations || null),
      bonusPriority: nextPatch.bonusPriority !== undefined ? (nextPatch.bonusPriority || null) : (existing.bonusPriority || null),																																														   
      iptvGfxId: nextPatch.iptvGfxId !== undefined ? nextPatch.iptvGfxId : existing.iptvGfxId || null,
      selectTiming: nextPatch.selectTiming !== undefined ? cleanMulti(nextPatch.selectTiming) : cleanMulti(existing.selectTiming),
      selectLocation: nextPatch.selectLocation !== undefined ? cleanMulti(nextPatch.selectLocation) : cleanMulti(existing.selectLocation),
      bigGfxVideosId: nextPatch.bigGfxVideosId !== undefined ? nextPatch.bigGfxVideosId : existing.bigGfxVideosId || null,
      cornerGfxVideosId: nextPatch.cornerGfxVideosId !== undefined ? nextPatch.cornerGfxVideosId : existing.cornerGfxVideosId || null,
      ledAllTypesId: nextPatch.ledAllTypesId !== undefined ? nextPatch.ledAllTypesId : existing.ledAllTypesId || null,
      bigTvcId: nextPatch.bigTvcId !== undefined ? nextPatch.bigTvcId : existing.bigTvcId || null,
      cornerTvcId: nextPatch.cornerTvcId !== undefined ? nextPatch.cornerTvcId : existing.cornerTvcId || null,
      mcgTiledLedId: nextPatch.mcgTiledLedId !== undefined ? nextPatch.mcgTiledLedId : existing.mcgTiledLedId || null,
      marvelTiledLedId: nextPatch.marvelTiledLedId !== undefined ? nextPatch.marvelTiledLedId : existing.marvelTiledLedId || null,
      superFullId: nextPatch.superFullId !== undefined ? nextPatch.superFullId : existing.superFullId || null,
      super169Id: nextPatch.super169Id !== undefined ? nextPatch.super169Id : existing.super169Id || null,
      superSideId: nextPatch.superSideId !== undefined ? nextPatch.superSideId : existing.superSideId || null,
      mcgInterchangeId: nextPatch.mcgInterchangeId !== undefined ? nextPatch.mcgInterchangeId : existing.mcgInterchangeId || null,
      marvelInterchangeId: nextPatch.marvelInterchangeId !== undefined ? nextPatch.marvelInterchangeId : existing.marvelInterchangeId || null,
      marvel360Id: nextPatch.marvel360Id !== undefined ? nextPatch.marvel360Id : existing.marvel360Id || null,
      mcg360Id: nextPatch.mcg360Id !== undefined ? nextPatch.mcg360Id : existing.mcg360Id || null,
      // ✅ Team List player number
      playerNumber: nextPatch.playerNumber != null ? nextPatch.playerNumber : existing.playerNumber || "",
      // ✅ Track who made this edit (VenueFlow/Supabase user)
      venueFlowEditor: editorName,
    };

    // ✅ Register pending field protection — prevents polling from overwriting these values
    registerPendingSave(recordId, nextPatch);

    // ✅ Save to server - polling blocked until complete
    api.update(recordId, payload)
      .then(() => {
        // Update asset cache in background
        const newIds = Object.values(nextPatch).filter((v) => typeof v === "string" && v.startsWith("rec"));
        if (newIds.length) {
          import("./airtable").then(({ fetchAssetsByIds }) => {
            fetchAssetsByIds(newIds).then(updateCache).catch(() => {});
          }).catch(() => {});
        }

        // ✅ Direction 1: Sync source → operator views (background, non-blocking)
        if (shouldSyncToOperator({ isTeam, isInGame, isLed, isIptv })) {
          const mergedItem = { ...existing, ...nextPatch };
          const sourceTableId = getSourceTableIdForMode({ isTeam });
          syncSourceToOperatorViews(recordId, mergedItem, {
            venue,
            sourceTableId,
            lastFingerprint: existing._lastAssetFingerprint || '',
          }).then((syncResult) => {
            if (syncResult && !syncResult.skipped) {
              console.log('[App] Operator sync complete:', syncResult);
              syncedRecordIdsRef.current.add(recordId);
            }
          }).catch((err) => {
            console.warn('[App] Operator sync failed (non-blocking):', err);
          });
        }
      })
      .catch((e) => {
        showAlert("Save failed: " + (e?.message || e), "error");
        load({ silent: true });
      })
      .finally(() => {
        isSavingRef.current = false;
      });
  }

  // ============================================================================
  // VIEW OPTIONS
  // ============================================================================
  
  const viewOptions = useMemo(() => {
    if (isTeam) {
      // ✅ Team List uses venue-based views instead of fixture views
      let teamViews = TEAM_LIST_VIEWS;
      // Filter by allowed venues for venue_ops
      if (isVenueOps && allowedVenues?.length > 0) {
        teamViews = filterViewsByVenue(teamViews, allowedVenues);
      }
      return teamViews.map((v) => ({ value: v, label: v }));
    }
    // ✅ Use dynamic views (already filtered for venue_ops in useEffect)
    if (dynamicViews.length > 0) {
      return dynamicViews.map((v) => ({ value: v, label: v }));
    }
    // Fallback while loading - show loading state instead of hardcoded view
    return [{ value: "", label: "Loading views..." }];
  }, [isTeam, dynamicViews, isVenueOps, allowedVenues, filterViewsByVenue]);

  function setQuery(next) {
    const p = new URLSearchParams(window.location.search);
    if (next.mode != null) p.set("mode", next.mode);
    if (next.view != null) {
      p.set("view", next.view);
      // ✅ Use dynamic fixture key conversion
      const mapped = viewNameToFixtureKey(next.view);
      if (mapped) p.set("fixture", mapped);
      // ✅ Sync to sessionStorage so Operator View picks it up
      try { sessionStorage.setItem('venueflow_selected_match', next.view); } catch (e) {}
    }
    if (next.fixture != null) p.set("fixture", next.fixture);
    window.location.search = `?${p.toString()}`;
  }

  const baseParams = useMemo(() => {
    const p = new URLSearchParams(window.location.search);
    if (isTeam) {
      // ✅ Team List: keep current view, no fixture needed
      p.set("view", viewName);
      p.delete("fixture"); // Team List doesn't use fixtures
    } else {
      p.set("fixture", fixtureKey);
      if (viewName) p.set("view", viewName);
    }
    return p;
  }, [fixtureKey, viewName, isTeam]);

  const statusLine = status ? status : saving ? "Saving…" : "";

  const defaultCategory = CATEGORY_ORDER.length > 0 ? CATEGORY_ORDER[0] : 
    (isTeam ? "Team List" : isIptv ? "General Content All Areas" : "Pre-Game");

  // ============================================================================
  // RENDER
  // ============================================================================
  
  // ✅ Show loading state while views are being resolved (prevents jitter)
  if (!viewsResolved && !isTeam) {
    return (
      <AuthGate>
        <div style={{ 
          background: THEME.surface2, 
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          gap: 16,
        }}>
          <div style={{
            width: 40,
            height: 40,
            border: `3px solid ${THEME.line}`,
            borderTopColor: THEME.primary,
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
          }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <span style={{ color: THEME.textSecondary, fontSize: 14 }}>Loading MatchFlow...</span>
        </div>
      </AuthGate>
    );
  }
  
  return (
    <AuthGate>
    <div style={{ background: THEME.surface2, minHeight: "100vh" }}>
      {/* Header - flush to edges */}
      <Header
        fixtureKey={fixtureKey}
        viewName={viewName}
        viewOptions={viewOptions}
        isTeam={isTeam}
        isTvc={isTvc}
        tvcHeaderInfo={tvcHeaderInfo}
        onTvcVenueChange={setTvcVenue}
        isCommLed={isCommLed}
        ledHeaderInfo={ledHeaderInfo}
        onLedVenueChange={setLedVenue}
        enableViewDropdown={true}
        modeLabel={modeLabel}
        statusLine={statusLine}
        onViewChange={(view) => setQuery({ view })}
        onNewRow={isMatchLocked ? null : () => openAdd(defaultCategory)}
        baseParams={baseParams}
        tableNames={tableNames}
        // ✅ NEW: Subheader props
        saving={saving}
        lastSaved={lastSaved}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchMatchCount={countMatches(items, searchTerm, assetCache)}
        onOpenGallery={() => setIsGalleryOpen(true)}
        onAddContent={isMatchLocked ? null : () => setAddingCategory("__NEW__")}
        // ✅ NEW: Undo props
        onUndo={isMatchLocked ? null : performUndo}
        canUndo={isMatchLocked ? false : canUndo}
        // ✅ NEW: Clone match + Bulk swap
        onCloneMatch={isMatchLocked ? null : (!isTeam && !isTvc && !isCommLed ? () => setIsCloneModalOpen(true) : null)}
        onBulkSwap={isMatchLocked ? null : (!isTvc && !isCommLed ? () => setIsBulkSwapModalOpen(true) : null)}
      />

      {/* ✅ Match Lock Banner — shown when current mode is locked for non-Lumen8 users */}
      {isMatchLocked && (
        <div style={{
          margin: '0 18px',
          padding: '16px 20px',
          background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.12) 0%, rgba(251, 191, 36, 0.06) 100%)',
          border: '1px solid rgba(251, 191, 36, 0.3)',
          borderRadius: 10,
          display: 'flex',
          alignItems: 'center',
          gap: 14,
        }}>
          <div style={{
            width: 40, height: 40, borderRadius: 10,
            background: 'rgba(251, 191, 36, 0.15)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fbbf24" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#fbbf24', marginBottom: 4 }}>
              Your match is now locked
            </div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', lineHeight: 1.5 }}>
              This table has been locked by the Lumen8 team. If you need to edit or submit new content, please contact your Lumen8 coordinator.
            </div>
          </div>
        </div>
      )}

      {/* Content area - with padding */}
      <div style={{ padding: "0 18px 18px 18px", maxWidth: 1900, margin: "0 auto", overflowX: "hidden" }}>

        {/* ✅ TVC Mode — club playlist management */}
        {isTvc ? (
          <div style={{ marginTop: 16 }}>
            <TVCPlaylistView
              selectedRound={tvcHeaderInfo.selectedRound}
              onHeaderInfoChange={setTvcHeaderInfo}
            />
          </div>
        ) : isCommLed ? (
          <div style={{ marginTop: 16 }}>
            <LEDPlaylistView
              selectedRound={ledHeaderInfo.selectedRound}
              onHeaderInfoChange={setLedHeaderInfo}
            />
          </div>
        ) : (
        <>
        {/* ✅ Confetti celebration */}
        <Confetti trigger={confettiTrigger} />

        {/* ✅ Content Pipeline Stats + Tips */}
        {items.length > 0 && (() => {
          // Exclude locked example/placeholder rows from all tallies
          const realItems = items.filter(i => !String(i.contentStatus || '').toLowerCase().includes('example content'));
          if (realItems.length === 0) return null;
          
          const cgItems = realItems.filter(i => i.bigGfxVideosId || i.cornerGfxVideosId || i.superFullId || i.super169Id || i.superSideId);
          const ledItems = realItems.filter(i => i.ledAllTypesId);
          const iptvItems = realItems.filter(i => i.iptvGfxId);
          const approvedItems = realItems.filter(i => i.contentStatus === 'Approved by Lumen8');
          const rejectedItems = realItems.filter(i => i.contentStatus === 'Rejected');
          const unlockedItems = realItems.filter(i => i.contentStatus === 'Content Unlocked');
          const newItems = realItems.filter(i => !i.contentStatus || i.contentStatus === 'New Submission' || i.contentStatus === 'Ready for Testing');
          
          const cgTotal = cgItems.length;
          const ledTotal = ledItems.length;
          const iptvTotal = iptvItems.length;
          
          const allDone = approvedItems.length === realItems.length && realItems.length > 0;
          
          const cardStyle = {
            flex: '1 1 0',
            minWidth: 120,
            padding: '14px 16px',
            borderRadius: 12,
            background: '#1a1a1d',
            border: 'none',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            transition: 'box-shadow 0.2s, transform 0.15s',
            cursor: 'default',
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
          };
          
          const numStyle = (color) => ({
            fontSize: 26, fontWeight: 800, color, letterSpacing: '-0.5px', lineHeight: 1,
          });
          
          const labelStyle = {
            fontSize: 11, fontWeight: 600, color: '#e4e4e7', lineHeight: 1.3,
          };

          const hintStyle = {
            fontSize: 9, color: '#6b6b78', marginTop: 2,
          };

          // CSV export helper
          const exportCSV = () => {
            const header = ['Name', 'Category', 'Content Status', 'CG Asset (Big Screen)', 'LED Asset', 'Lumen8 ID', 'Has Content', 'Club Instructions'];
            const rows = realItems.map(i => [
              i.name || '',
              i.category || '',
              i.contentStatus || 'New Submission',
              i.bigGfxVideosId ? 'Yes' : 'No',
              i.ledAllTypesId ? 'Yes' : 'No',
              i.lumen8Id || '',
              (i.bigGfxVideosId || i.ledAllTypesId || i.iptvGfxId) ? 'Yes' : 'NO — WILL NOT SYNC TO OPERATOR',
              i.clubInstructions || '',
            ]);
            const csv = [header, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
            const blob = new Blob([csv], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `MatchFlow_${currentMode}_${viewName || 'export'}_${new Date().toISOString().slice(0,10)}.csv`;
            a.click();
            URL.revokeObjectURL(url);
          };

          // Detect rows with instructions but no assets — these won't reach the operator
          const emptyRowsWithNotes = realItems.filter(i => 
            (i.clubInstructions && i.clubInstructions.trim()) && !i.bigGfxVideosId && !i.ledAllTypesId && !i.iptvGfxId
          );

          // Detect rows with archived content — allocated assets that have been archived from library
          const archivedRows = realItems.map(i => {
            const ids = [
              i.bigGfxVideosId, i.ledAllTypesId, i.cornerGfxVideosId,
              i.bigTvcId, i.cornerTvcId, i.iptvGfxId,
              i.mcgTiledLedId, i.marvelTiledLedId, i.superFullId,
              i.super169Id, i.superSideId, i.mcgInterchangeId,
              i.marvelInterchangeId, i.marvel360Id, i.mcg360Id,
            ].filter(Boolean);
            const archived = ids.map(id => assetCache.get(id)).filter(a => a && a.archived);
            if (archived.length === 0) return null;
            return { ...i, archivedAssets: archived };
          }).filter(Boolean);

          // Count total alerts for the collapsible header
          const alertCount = (rejectedItems.length > 0 && !allDone ? 1 : 0)
            + (emptyRowsWithNotes.length > 0 ? 1 : 0)
            + (archivedRows.length > 0 ? 1 : 0);

          return (
            <div style={{ marginTop: 16, marginBottom: 8 }}>
              {/* Stats Cards Row — mode-aware */}
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
                {/* CG Total — shown in all modes except IPTV */}
                {currentMode !== 'iptv' && (
                  <div style={{ ...cardStyle, borderLeft: '4px solid #3b82f6' }}>
                    <span style={numStyle('#3b82f6')}>{cgTotal}</span>
                    <span style={labelStyle}>CG / Graphics</span>
                    <span style={hintStyle}>{venue === 'Marvel Stadium' ? 'SuperScreen + Corner' : 'Big Screen - GFX & Videos'}</span>
                  </div>
                )}

                {/* LED Total — shown in all modes except IPTV */}
                {currentMode !== 'iptv' && (
                  <div style={{ ...cardStyle, borderLeft: '4px solid #f59e0b' }}>
                    <span style={numStyle('#f59e0b')}>{ledTotal}</span>
                    <span style={labelStyle}>LED Assets</span>
                    <span style={hintStyle}>LED - All Types</span>
                  </div>
                )}

                {/* IPTV Total — shown only in IPTV mode */}
                {currentMode === 'iptv' && (
                  <div style={{ ...cardStyle, borderLeft: '4px solid #8b5cf6' }}>
                    <span style={numStyle('#8b5cf6')}>{iptvTotal}</span>
                    <span style={labelStyle}>IPTV Assets</span>
                    <span style={hintStyle}>IPTV GFX</span>
                  </div>
                )}

                {/* Built / Approved */}
                <div style={{ ...cardStyle, borderLeft: '4px solid #22c55e', ...(allDone ? { background: '#0a2e1a' } : {}) }}>
                  <span style={numStyle('#22c55e')}>{approvedItems.length}</span>
                  <span style={labelStyle}>{allDone ? '✓ All Loaded & Locked' : 'Loaded for Playout'}</span>
                  <span style={hintStyle}>Locked — content cannot be edited</span>
                </div>

                {/* Needs Action */}
                {newItems.length > 0 && (
                  <div style={{ ...cardStyle, borderLeft: '4px solid #f97316' }}>
                    <span style={numStyle('#f97316')}>{newItems.length}</span>
                    <span style={labelStyle}>Pending Content</span>
                    <span style={hintStyle}>Pending — not yet loaded for playout</span>
                  </div>
                )}

                {/* Rejected */}
                {rejectedItems.length > 0 && (
                  <div style={{ ...cardStyle, borderLeft: '4px solid #ef4444', background: '#2e0a0a' }}>
                    <span style={numStyle('#ef4444')}>{rejectedItems.length}</span>
                    <span style={labelStyle}>Rejected</span>
                    <span style={hintStyle}>Sent back to club for revision</span>
                  </div>
                )}

                {/* Content Unlocked */}
                {unlockedItems.length > 0 && (
                  <div style={{ ...cardStyle, borderLeft: '4px solid #a855f7', background: '#1e0a2e' }}>
                    <span style={numStyle('#a855f7')}>{unlockedItems.length}</span>
                    <span style={labelStyle}>Content Changed</span>
                    <span style={hintStyle}>Club updated — needs re-check</span>
                  </div>
                )}
              </div>

              {/* Progress Bar + Export */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <div style={{ flex: 1, height: 6, borderRadius: 3, background: THEME.line, overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', borderRadius: 3,
                    width: `${realItems.length > 0 ? Math.round((approvedItems.length / realItems.length) * 100) : 0}%`,
                    background: allDone ? '#22c55e' : 'linear-gradient(90deg, #22c55e, #22BA91)',
                    transition: 'width 0.5s ease',
                  }} />
                </div>
                <span style={{ fontSize: 11, fontWeight: 700, color: allDone ? '#22c55e' : THEME.textSecondary, whiteSpace: 'nowrap' }}>
                  {realItems.length > 0 ? Math.round((approvedItems.length / realItems.length) * 100) : 0}% complete
                </span>
                <button onClick={exportCSV} title="Export content rows to CSV for verification"
                  style={{
                    fontSize: 10, fontWeight: 600, padding: '4px 12px', borderRadius: 6,
                    border: `1px solid ${THEME.line}`, background: THEME.surface, color: THEME.textSecondary,
                    cursor: 'pointer', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 4,
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = THEME.surface2; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = THEME.surface; }}
                >📥 Export CSV</button>
              </div>

              {/* ─── Status Summary ─── */}
              {allDone && (
                <div style={{
                  padding: '10px 14px', borderRadius: 10,
                  background: '#1a1a1d', borderLeft: '4px solid #22c55e',
                  fontSize: 12, color: '#c4c4cc', lineHeight: 1.5,
                  display: 'flex', alignItems: 'flex-start', gap: 8,
                }}>
                  <span style={{ fontSize: 14, flexShrink: 0 }}>✅</span>
                  <span>
                    <strong style={{ color: '#22c55e' }}>All content loaded and locked!</strong>{' '}
                    Your content has been loaded into the playout system. Rows are now locked and cannot be edited.
                    Contact your Lumen8 content manager if you need to make changes.
                  </span>
                </div>
              )}
              {!allDone && newItems.length > 0 && alertCount === 0 && (
                <div style={{
                  padding: '10px 14px', borderRadius: 10,
                  background: '#1a1a1d', borderLeft: '4px solid #f59e0b',
                  fontSize: 12, color: '#c4c4cc', lineHeight: 1.5,
                  display: 'flex', alignItems: 'flex-start', gap: 8,
                }}>
                  <span style={{ fontSize: 14, flexShrink: 0 }}>💡</span>
                  <span>
                    <strong style={{ color: '#f59e0b' }}>You have {newItems.length} content row{newItems.length !== 1 ? 's' : ''} allocated for submission.</strong>{' '}
                    When your content is loaded into the playout system, the content row will turn green and you will no longer be able to edit it.
                  </span>
                </div>
              )}

              {/* ─── Status Bar — issues or all-clear ─── */}
              {alertCount > 0 ? (() => {
                const totalIssues = rejectedItems.length + emptyRowsWithNotes.length + archivedRows.length;
                return (
                  <details style={{ marginTop: 6 }}>
                    <summary style={{
                      padding: '14px 18px', borderRadius: 10, cursor: 'pointer',
                      background: '#1a1a1d',
                      border: '1px solid rgba(249,115,22,0.35)',
                      fontSize: 13, color: '#c4c4cc', lineHeight: 1.5,
                      display: 'flex', alignItems: 'center', gap: 12,
                      listStyle: 'none', userSelect: 'none',
                    }}>
                      <span style={{ fontSize: 18 }}>⚠️</span>
                      <strong style={{ color: '#f97316', fontSize: 14 }}>{totalIssues} issue{totalIssues !== 1 ? 's' : ''} need{totalIssues === 1 ? 's' : ''} attention</strong>
                      <div style={{ display: 'flex', gap: 6, marginLeft: 4 }}>
                        {rejectedItems.length > 0 && (
                          <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 5, background: 'rgba(239,68,68,0.18)', color: '#f87171', border: '1px solid rgba(239,68,68,0.4)' }}>
                            {rejectedItems.length} rejected
                          </span>
                        )}
                        {emptyRowsWithNotes.length > 0 && (
                          <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 5, background: 'rgba(249,115,22,0.18)', color: '#fb923c', border: '1px solid rgba(249,115,22,0.4)' }}>
                            {emptyRowsWithNotes.length} no content
                          </span>
                        )}
                        {archivedRows.length > 0 && (
                          <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 5, background: 'rgba(239,68,68,0.18)', color: '#f87171', border: '1px solid rgba(239,68,68,0.4)' }}>
                            {archivedRows.length} archived
                          </span>
                        )}
                      </div>
                      <span style={{
                        marginLeft: 'auto', fontSize: 11, fontWeight: 700,
                        padding: '4px 14px', borderRadius: 6,
                        background: 'rgba(249,115,22,0.18)', color: '#fb923c',
                        border: '1px solid rgba(249,115,22,0.4)',
                        cursor: 'pointer',
                      }}>▼ Click to expand/collapse</span>
                    </summary>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
                      {/* Rejected */}
                      {rejectedItems.length > 0 && !allDone && (
                        <div style={{
                          padding: '12px 14px', borderRadius: 8,
                          background: '#222230', borderLeft: '4px solid #ef4444',
                          fontSize: 12, color: '#c4c4cc', lineHeight: 1.5,
                        }}>
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                            <span style={{ fontSize: 14, flexShrink: 0 }}>❗</span>
                            <div>
                              <strong style={{ color: '#ef4444' }}>{rejectedItems.length} content row{rejectedItems.length !== 1 ? 's' : ''} rejected.</strong>{' '}
                              Update the asset and resubmit. The review process will restart automatically.
                            </div>
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10, marginLeft: 24 }}>
                            {rejectedItems.slice(0, 8).map((item, idx) => {
                              const ids = [item.bigGfxVideosId, item.ledAllTypesId, item.cornerGfxVideosId].filter(Boolean);
                              const lumenIds = ids.map(id => { const a = assetCache.get(id); return a ? (a.lumenId || a.title || id) : id; });
                              return (
                                <span key={idx} style={{
                                  display: 'inline-flex', alignItems: 'center', gap: 4,
                                  padding: '4px 10px', borderRadius: 6,
                                  background: 'rgba(239,68,68,0.15)',
                                  border: '1px solid rgba(239,68,68,0.4)',
                                  color: '#f87171', fontSize: 11, fontWeight: 700,
                                }}>
                                  ❗ {item.name || 'Unnamed'}{lumenIds.length > 0 && ` → ${lumenIds.join(', ')}`}
                                </span>
                              );
                            })}
                            {rejectedItems.length > 8 && (
                              <span style={{
                                padding: '4px 10px', borderRadius: 6,
                                background: 'rgba(239,68,68,0.08)',
                                border: '1px dashed rgba(239,68,68,0.3)',
                                color: '#ef4444', fontSize: 11, fontWeight: 600,
                              }}>
                                +{rejectedItems.length - 8} more
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* No content */}
                      {emptyRowsWithNotes.length > 0 && (
                        <div style={{
                          padding: '10px 14px', borderRadius: 8,
                          background: '#222230', borderLeft: '4px solid #f97316',
                          fontSize: 12, color: '#c4c4cc', lineHeight: 1.5,
                        }}>
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                            <span style={{ fontSize: 14, flexShrink: 0 }}>🚨</span>
                            <span>
                              <strong style={{ color: '#f97316' }}>{emptyRowsWithNotes.length} row{emptyRowsWithNotes.length !== 1 ? 's' : ''} — no content allocated.</strong>{' '}
                              Won't reach the operator. Allocate a CG or LED asset from your library.
                            </span>
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 8, marginLeft: 22 }}>
                            {emptyRowsWithNotes.slice(0, 8).map((item, idx) => (
                              <span key={idx} style={{
                                display: 'inline-flex', alignItems: 'center', gap: 4,
                                padding: '3px 8px', borderRadius: 5,
                                background: 'rgba(249, 115, 22, 0.18)',
                                border: '1px solid rgba(249, 115, 22, 0.5)',
                                color: '#fb923c', fontSize: 10, fontWeight: 700,
                              }}>
                                ⚠ {item.name || 'Unnamed'}
                              </span>
                            ))}
                            {emptyRowsWithNotes.length > 8 && (
                              <span style={{ padding: '3px 8px', borderRadius: 5, background: 'rgba(249,115,22,0.10)', border: '1px dashed rgba(249,115,22,0.4)', color: '#f97316', fontSize: 10, fontWeight: 600 }}>
                                +{emptyRowsWithNotes.length - 8} more
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Archived */}
                      {archivedRows.length > 0 && (
                        <div style={{
                          padding: '10px 14px', borderRadius: 8,
                          background: '#222230', borderLeft: '4px solid #ef4444',
                          fontSize: 12, color: '#c4c4cc', lineHeight: 1.5,
                        }}>
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                            <span style={{ fontSize: 14, flexShrink: 0 }}>📦</span>
                            <span>
                              <strong style={{ color: '#ef4444' }}>{archivedRows.length} row{archivedRows.length !== 1 ? 's' : ''} — archived content.</strong>{' '}
                              <strong style={{ color: '#fbbf24' }}>Will not go out for playout.</strong> Replace with active content.
                            </span>
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginTop: 8, marginLeft: 22 }}>
                            {archivedRows.slice(0, 6).map((item, idx) => {
                              const archivedIds = item.archivedAssets.map(a => a.lumenId || a.title || a.id);
                              return (
                                <div key={idx} style={{
                                  display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
                                }}>
                                  <span style={{
                                    padding: '3px 8px', borderRadius: 5,
                                    background: 'rgba(239,68,68,0.15)',
                                    border: '1px solid rgba(239,68,68,0.4)',
                                    color: '#f87171', fontSize: 10, fontWeight: 700,
                                  }}>
                                    📦 {item.name || 'Unnamed'}
                                  </span>
                                  {archivedIds.map((lid, j) => (
                                    <span key={j} style={{
                                      padding: '2px 6px', borderRadius: 4,
                                      background: 'rgba(239,68,68,0.08)',
                                      border: '1px dashed rgba(239,68,68,0.3)',
                                      color: '#fca5a5', fontSize: 9, fontWeight: 600,
                                      fontFamily: 'JetBrains Mono, monospace',
                                    }}>
                                      {lid}
                                    </span>
                                  ))}
                                </div>
                              );
                            })}
                            {archivedRows.length > 6 && (
                              <span style={{ padding: '3px 8px', borderRadius: 5, background: 'rgba(239,68,68,0.08)', border: '1px dashed rgba(239,68,68,0.3)', color: '#ef4444', fontSize: 10, fontWeight: 600 }}>
                                +{archivedRows.length - 6} more rows
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </details>
                );
              })() : (
                <div style={{
                  padding: '14px 18px', borderRadius: 10, marginTop: 6,
                  background: '#1a1a1d',
                  border: '1px solid rgba(34,197,94,0.3)',
                  fontSize: 13, color: '#c4c4cc', lineHeight: 1.5,
                  display: 'flex', alignItems: 'center', gap: 12,
                }}>
                  <span style={{ fontSize: 18 }}>✅</span>
                  <strong style={{ color: '#22c55e', fontSize: 14 }}>All content up to date</strong>
                  <span style={{ fontSize: 12, color: '#6b6b78' }}>— no issues detected</span>
                </div>
              )}
            </div>
          );
        })()}

        <div
          ref={tableScrollRef}
          className="table-scroll-container"
          style={{
            paddingBottom: 12,
            marginTop: 16,
            position: "relative",
            overflowX: "auto",
            overflowY: "visible", // Let browser handle vertical scroll
            // Removed maxHeight - let page scroll naturally
          }}
        >
          <DndContext
            collisionDetection={closestCenter}
            onDragStart={onDragStart}
            onDragCancel={onDragCancel}
            onDragEnd={onDragEnd}
          >
            {usePhasing && phasedCategories ? (
              // ✅ GAME TIMELINE: Render with phase headers
              phasedCategories.map(({ phase, categories }) => (
                <React.Fragment key={phase}>
                  {/* Phase Header with Entitlements Tooltip */}
                  <PhaseHeader phase={phase} theme={THEME} />

                  {/* Categories within this phase */}
                  {categories.map((cat) => {
                    const list = grouped.get(cat) || [];
                    // Venue ops: read-only for all categories EXCEPT Venue Content
                    const isReadOnly = isMatchLocked || (isVenueOps && cat !== VENUE_CONTENT_CATEGORY);
                    return (
                      <CategorySection
                        key={cat}
                        category={cat}
                        items={list}
                        visibleCols={visibleCols}
                        stickyLeftMap={stickyLeftMap}
                        template={template}
                        minWidth={MIN_WIDTH}
                        assetCache={assetCache}
                        isBusy={saving || showModal || !!editingItem || !!addingCategory}
                        editingCell={editingCell}
                        setEditingCell={setEditingCell}
                        onCopy={isReadOnly ? null : handleCopy}
                        onMove={isReadOnly ? null : handleMove}
                        onDelete={isReadOnly ? null : handleDelete}
                        onInlineSave={isReadOnly ? null : onInlineSave}
                        visibleKeys={visibleKeys}
                        isIptvMode={isIptv}
                        onCreateEmptyRow={isReadOnly ? null : createEmptyRowForCategory}
                        onCancelLastCreated={cancelLastInlineCreatedRow}
                        lastInlineCreatedId={lastInlineCreatedId}
                        saving={saving}
                        isLedMode={isLed}
                        onShuffleLed={isReadOnly ? null : shuffleLedSequence}
                        isRowLocked={isReadOnly ? () => true : isRowLocked}
                        iptvOptions={iptvOptions}
                        selectedRowIds={selectedRowIds}
                        onToggleRowSelection={isReadOnly ? null : toggleRowSelection}
                        onClearSelection={clearSelection}
                        bulkCopyTargetCategories={bulkCopyTargetCategories}
                        onBulkCopyTargetChange={setBulkCopyTargetCategories}
                        onBulkCopy={isReadOnly ? null : handleBulkCopy}
                        onBulkMove={isReadOnly ? null : handleBulkMove}
                        onBulkDelete={isReadOnly ? null : handleBulkDelete}
                        allCategories={CATEGORY_ORDER}
                        showActionBar={categoriesWithSelections.has(cat)}
                        readOnly={isReadOnly}
                        searchTerm={searchTerm}
                      />
                    );
                  })}
                </React.Fragment>
              ))
            ) : (
              // ✅ OTHER MODES: Render without phase headers (current behavior)
              CATEGORY_ORDER.map((cat) => {
                const list = grouped.get(cat) || [];
                // Venue ops: read-only for all categories EXCEPT Venue Content
                const isReadOnly = isMatchLocked || (isVenueOps && cat !== VENUE_CONTENT_CATEGORY);
                return (
                  <CategorySection
                    key={cat}
                    category={cat}
                    items={list}
                    visibleCols={visibleCols}
                    stickyLeftMap={stickyLeftMap}
                    template={template}
                    minWidth={MIN_WIDTH}
                    assetCache={assetCache}
                    isBusy={saving || showModal || !!editingItem || !!addingCategory}
                    editingCell={editingCell}
                    setEditingCell={setEditingCell}
                    onCopy={isReadOnly ? null : handleCopy}
                    onMove={isReadOnly ? null : handleMove}
                    onDelete={isReadOnly ? null : handleDelete}
                    onInlineSave={isReadOnly ? null : onInlineSave}
                    visibleKeys={visibleKeys}
                    isIptvMode={isIptv}
                    onCreateEmptyRow={isReadOnly ? null : createEmptyRowForCategory}
                    onCancelLastCreated={cancelLastInlineCreatedRow}
                    lastInlineCreatedId={lastInlineCreatedId}
                    saving={saving}
                    isLedMode={isLed}
                    onShuffleLed={isReadOnly ? null : shuffleLedSequence}
                    isRowLocked={isReadOnly ? () => true : isRowLocked}
                    iptvOptions={iptvOptions}
                    selectedRowIds={selectedRowIds}
                    onToggleRowSelection={isReadOnly ? null : toggleRowSelection}
                    onClearSelection={clearSelection}
                    bulkCopyTargetCategories={bulkCopyTargetCategories}
                    onBulkCopyTargetChange={setBulkCopyTargetCategories}
                    onBulkCopy={isReadOnly ? null : handleBulkCopy}
                    onBulkMove={isReadOnly ? null : handleBulkMove}
                    onBulkDelete={isReadOnly ? null : handleBulkDelete}
                    allCategories={CATEGORY_ORDER}
                    showActionBar={categoriesWithSelections.has(cat)}
                    searchTerm={searchTerm}
                    readOnly={isReadOnly}
                  />
                );
              })
            )}
          </DndContext>
        </div>
        </>
        )}
      </div>

      {editingItem && (
        <EditModal
          item={editingItem}
          onClose={() => setEditingItem(null)}
          onSave={handleModalSave}
          isLedMode={isLed}
          isIptvMode={isIptv}
          assetCache={assetCache}
          iptvOptions={iptvOptions}
        />
      )}

      {addingCategory && (
        <AddModal
          initialCategory={addingCategory === "__NEW__" ? "" : addingCategory}
          categories={addingCategory === "__NEW__" ? definedCategories : CATEGORY_ORDER}
          onClose={() => setAddingCategory(null)}
          onCreate={handleCreate}
          isLedMode={isLed}
          isIptvMode={isIptv}
          isTeamMode={isTeam}
          isInGameMode={isInGame}
          venue={venue}
          iptvOptions={iptvOptions}
          assetCache={assetCache}
        />
      )}

      {/* ✅ Asset Gallery popup */}
      <AssetGallery
        isOpen={isGalleryOpen}
        onClose={() => setIsGalleryOpen(false)}
        assetCache={assetCache}
      />

      {/* ✅ Clone Previous Match Modal */}
      <CloneMatchModal
        isOpen={isCloneModalOpen}
        onClose={() => setIsCloneModalOpen(false)}
        dynamicViews={dynamicViews}
        currentViewName={viewName}
        api={api}
        fixtureForWrites={fixtureForWrites}
        defaultStatus={DEFAULT_NEW_STATUS}
        editorName={editorName}
        onComplete={() => load({ silent: true })}
        isTeam={isTeam}
        viewNameToFixtureKey={viewNameToFixtureKey}
        onSyncRow={shouldSyncToOperator({ isTeam, isInGame, isLed, isIptv }) ? (recordId, item) => {
          const sourceTableId = getSourceTableIdForMode({ isTeam });
          return syncSourceToOperatorViews(recordId, item, {
            venue,
            sourceTableId,
            lastFingerprint: '',
          });
        } : null}
      />

      {/* ✅ Bulk Asset Swap Modal */}
      <BulkAssetSwapModal
        isOpen={isBulkSwapModalOpen}
        onClose={() => setIsBulkSwapModalOpen(false)}
        items={items}
        assetCache={assetCache}
        api={api}
        onComplete={() => load({ silent: true })}
      />

      {/* ✅ Floating Slack Chat Widget - DISABLED (Tawk not fit for purpose, keeping code for future Airtable chat) */}
      {/* <SlackChatWidget viewName={viewName} /> */}

      {/* ✅ Sticky horizontal scrollbar at bottom of viewport */}
      <StickyHorizontalScroll containerRef={tableScrollRef} />

      {/* ✅ Version update prompt — notifies users when a new deployment is available */}
      <UpdateBanner />
    </div>
    </AuthGate>
  );
}
